import { theme as antTheme } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { formatNumber } from '../../lib/format';

/** One line on the chart. `data` values are plain numbers (display-only — the
 * caller converts the API's decimal strings with `Number()` once, here at the
 * edge, never in money arithmetic). */
export interface TrendSeries {
  data: number[];
  color: string;
  dashed?: boolean;
}

interface TrendChartProps {
  /** X labels, one per data point (all series share the same x positions). */
  months: string[];
  series: TrendSeries[];
  height?: number;
  /** Label every Nth month tick (2 keeps a narrow card readable). */
  xLabelEvery?: number;
}

/** "Nice" number for axis rounding (Heckbert) — the nearest 1/2/5/10 ×10ⁿ. */
function niceNum(value: number, round: boolean): number {
  const exp = Math.floor(Math.log10(value || 1));
  const frac = value / 10 ** exp;
  const niceFrac = round
    ? frac < 1.5 ? 1 : frac < 3 ? 2 : frac < 7 ? 5 : 10
    : frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return niceFrac * 10 ** exp;
}

/** Rounded axis bounds + evenly-spaced tick values covering [min, max]. */
function niceScale(min: number, max: number, tickCount = 4): { lo: number; hi: number; ticks: number[] } {
  if (max === min) max = min + 1;
  const range = niceNum(max - min, false);
  const step = niceNum(range / tickCount, true);
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Math.round(v));
  return { lo, hi, ticks };
}

/** Tracks the rendered width of a block so the SVG can be drawn at real pixel
 * dimensions (viewBox 1:1 with the element), keeping text and strokes crisp at
 * any card width — no charting dependency, no aspect-ratio distortion. */
function useElementWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

/**
 * A minimal line chart used by the dashboard's Vânzări / Încasări widgets. One
 * or two series (current year solid, previous year dashed), light horizontal
 * gridlines with y-axis labels, and month ticks along the bottom.
 */
export function TrendChart({ months, series, height = 180, xLabelEvery = 1 }: TrendChartProps) {
  const {
    token: { colorBorderSecondary, colorTextTertiary },
  } = antTheme.useToken();
  const { ref, width } = useElementWidth();

  const padL = 52;
  const padR = 12;
  const padT = 12;
  const padB = 22;
  const plotW = Math.max(1, width - padL - padR);
  const plotH = Math.max(1, height - padT - padB);

  const all = series.flatMap((s) => s.data);
  const dataMax = all.length ? Math.max(...all) : 1;
  const dataMin = all.length ? Math.min(0, ...all) : 0;
  const { lo, hi, ticks } = niceScale(dataMin, dataMax, 4);

  const n = months.length;
  const xFor = (i: number): number => padL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yFor = (v: number): number => padT + plotH * (1 - (v - lo) / (hi - lo || 1));

  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Grafic evoluție">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} y1={yFor(t)} x2={width - padR} y2={yFor(t)} stroke={colorBorderSecondary} strokeDasharray="3 3" />
            {t !== 0 ? (
              <text x={padL - 8} y={yFor(t) + 4} textAnchor="end" fontSize={11} fill={colorTextTertiary}>
                {formatNumber(t)}
              </text>
            ) : null}
          </g>
        ))}

        {series.map((s, si) => (
          <polyline
            key={si}
            fill="none"
            stroke={s.color}
            strokeWidth={2.5}
            strokeDasharray={s.dashed ? '5 5' : undefined}
            strokeLinejoin="round"
            strokeLinecap="round"
            points={s.data.map((v, i) => `${xFor(i)},${yFor(v)}`).join(' ')}
          />
        ))}

        {months.map((label, i) =>
          i % xLabelEvery === 0 ? (
            <text key={i} x={xFor(i)} y={height - 6} textAnchor="middle" fontSize={11} fill={colorTextTertiary}>
              {label}
            </text>
          ) : null,
        )}
      </svg>
    </div>
  );
}

/** Short Romanian month labels, index 0 = Ianuarie. */
export const MONTH_ABBR = ['Ian.', 'Feb.', 'Mar.', 'Apr.', 'Mai', 'Iun.', 'Iul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'] as const;
